import React from 'react';

/**
 * Crystal ball with an ornate golden filigree stand.
 * - Flat watercolor-style SVG (same aesthetic as tulips)
 * - Orb sits directly on the stand (no visible gap)
 * - Reflection sweep is clipped inside the orb circle
 * - No shadow, no center connecting piece
 */
export default function CrystalBall({ size = 64, animate = true }) {
  const uid = 'cb';
  return (
    <svg
      width={size}
      height={size * (95 / 60)}
      viewBox="0 0 60 95"
      xmlns="http://www.w3.org/2000/svg"
      className="inline-block crystal-ball-svg"
      aria-hidden="true"
    >
      <defs>
        <radialGradient id={`${uid}-body`} cx="42%" cy="38%" r="68%">
          <stop offset="0%" stopColor="#f4e6fb" stopOpacity="0.95" />
          <stop offset="55%" stopColor="#cdb2e0" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#8e6ab4" stopOpacity="0.95" />
        </radialGradient>

        <radialGradient id={`${uid}-shine`} cx="30%" cy="28%" r="25%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>

        <linearGradient id={`${uid}-sweep`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0" />
          <stop offset="50%" stopColor="#ffffff" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>

        <linearGradient id={`${uid}-gold`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#f7d176" />
          <stop offset="55%" stopColor="#e0a845" />
          <stop offset="100%" stopColor="#9b6a1c" />
        </linearGradient>

        <clipPath id={`${uid}-orbClip`}>
          <circle cx="30" cy="34" r="24" />
        </clipPath>
      </defs>

      {/* ====== Small ornate golden stand (half size, no shadow, no middle) ====== */}
      {/* Top ring (the orb rests directly on this — no gap) */}
      <g transform="translate(0, -2)">
        <path
          d="M18 60
             Q 30 65, 42 60
             Q 42 63, 40 64
             Q 30 66, 20 64
             Q 18 63, 18 60 Z"
          fill={`url(#${uid}-gold)`}
          stroke="#8b5d18"
          strokeWidth="0.5"
        />

        {/* Left curl leg — compact */}
        <path
          d="M20 61
             C 16 66, 14 72, 14 77
             C 14 80, 16 82, 18 82
             C 19 82, 19 81, 19 80
             C 17 79, 17 77, 18 75
             C 19 72, 22 70, 24 68
             C 24 65, 22 62, 20 61 Z"
          fill={`url(#${uid}-gold)`}
          stroke="#8b5d18"
          strokeWidth="0.5"
        />

        {/* Right curl leg (mirror) */}
        <path
          d="M40 61
             C 44 66, 46 72, 46 77
             C 46 80, 44 82, 42 82
             C 41 82, 41 81, 41 80
             C 43 79, 43 77, 42 75
             C 41 72, 38 70, 36 68
             C 36 65, 38 62, 40 61 Z"
          fill={`url(#${uid}-gold)`}
          stroke="#8b5d18"
          strokeWidth="0.5"
        />

        {/* Decorative rivets at the curl tops */}
        <circle cx="21" cy="65" r="1.4" fill={`url(#${uid}-gold)`} stroke="#8b5d18" strokeWidth="0.4" />
        <circle cx="39" cy="65" r="1.4" fill={`url(#${uid}-gold)`} stroke="#8b5d18" strokeWidth="0.4" />

        {/* Tiny feet at the bottom of each leg */}
        <ellipse cx="17" cy="83" rx="2" ry="1" fill="#c99050" />
        <ellipse cx="43" cy="83" rx="2" ry="1" fill="#c99050" />
      </g>

      {/* ====== Orb (sits directly on the ring) ====== */}
      <circle
        cx="30"
        cy="34"
        r="24"
        fill={`url(#${uid}-body)`}
        stroke="#8e6ab4"
        strokeWidth="0.8"
      />

      {/* Sweeping reflection, clipped to orb */}
      <g clipPath={`url(#${uid}-orbClip)`}>
        <rect
          className={animate ? 'crystal-ball-sweep' : ''}
          x="0"
          y="10"
          width="16"
          height="48"
          fill={`url(#${uid}-sweep)`}
          transform="translate(-20 0) rotate(20 8 34)"
          style={{ transformBox: 'view-box' }}
        />
        <circle cx="30" cy="34" r="18" fill="#ffffff" opacity="0.08" />
      </g>

      {/* Top shine highlights */}
      <ellipse cx="22" cy="24" rx="7" ry="4.5" fill={`url(#${uid}-shine)`} />
      <circle cx="38" cy="26" r="1.3" fill="#ffffff" opacity="0.85" />
    </svg>
  );
}
